import React from 'react'

// PRO-130 LEAF, not the `src/icons/antd-compat` barrel: this component is
// reached from the boot-static route fallback
// (`src/components/main/RouteChunkSkeleton`), and the barrel's
// `export * from '@ant-design/icons'` welds the package's whole glyph surface
// onto whatever chunk imports it. The leaf is still inside the icon seam, so
// the lucide backend stays where it is allowed to be named.
import LoadingOutlined from 'src/icons/antd-compat/glyphs/LoadingOutlined'
import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * shadcn's Spinner. Nothing reaches this file until someone flips the design
 * toggle (`src/design-mode`); `src/antd/Spin` is the dispatcher that chooses
 * between it and antd's own Spin.
 *
 * The captain's source, which this follows:
 *
 *   function Spinner({ className, ...props }: React.ComponentProps<"svg">) {
 *     return (
 *       <LoaderIcon
 *         role="status"
 *         aria-label="Loading"
 *         className={cn("size-4 animate-spin", className)}
 *         {...props}
 *       />
 *     )
 *   }
 *
 * FOUR DELIBERATE DEVIATIONS, each forced by this branch rather than chosen:
 *
 * 1. THE GLYPH COMES FROM THE ICON SEAM, not from `lucide-react`. AGENTS.md
 *    holds `lucide-react` at zero references outside `src/icons/antd-compat`
 *    (`src/antd/__tests__/seam-adoption.evidence.test.tsx` counts it), because
 *    a file outside that seam which imports lucide renders lucide with the
 *    toggle OFF — the one thing the whole design track promises cannot happen.
 *    So the loading glyph arrives as the seam's `LoadingOutlined` dispatcher,
 *    exactly as `src/antd/Button.shadcn.tsx` already does for a button's
 *    loading state. The seam maps it to lucide's `Loader` — the SPOKED
 *    WHEEL, which the package also exports as `LoaderIcon`, and which is the
 *    glyph shadcn's spinner CUSTOMIZATION variant renders. That is the one
 *    the captain asked for. It is NOT `LoaderCircle` (the arc shadcn's
 *    registry ships as its default, aliased `Loader2Icon`), which #978 shipped
 *    by mistake and this branch corrected. The two names have swapped meaning
 *    across lucide releases, so read the alias list in
 *    `node_modules/lucide-react` rather than trusting either name; the glyph
 *    lives in `src/icons/antd-compat/icon-map.txt`, one line, and the file
 *    beside it is generated.
 *
 * 2. NO `animate-spin`. The seam's `LoadingOutlined` is declared `spin` mode
 *    (`src/icons/antd-compat/icon-map.txt`), so its svg already carries
 *    `.anticon-spin` — `loadingCircle 1s infinite linear`, the same rotation
 *    `animate-spin` applies. Adding the utility on top would rotate the
 *    wrapper AND the svg, compounding to double speed; and the utility cannot
 *    be cancelled from here, because `src/icons/antd-compat/shadcn/styles.ts`
 *    injects its sheet UNLAYERED and an unlayered rule outranks every
 *    Tailwind layer regardless of specificity.
 *
 * 3. SIZE IS `text-*`, NOT `size-*`. The icon seam deliberately keeps antd's
 *    convention — the svg is `1em`, so an icon takes its size from
 *    `font-size` and every existing `style={{ fontSize }}` keeps working
 *    (`src/icons/antd-compat/shadcn/createIcon.tsx` says so). `size-4` would
 *    fix the wrapper box at 16px while leaving the glyph on whatever font-size
 *    it inherited. One lever, not two: `text-base` IS shadcn's `size-4`.
 *
 * 4. NO COLOUR. `.anticon { color: inherit }` lives in the seam's runtime
 *    sheet, which is UNLAYERED — so it outranks a `text-primary` utility on
 *    the same element no matter what the specificity says, and the spinner
 *    silently paints in whatever colour it inherited. Browser-confirmed: a
 *    `text-primary` here rendered BLACK. The colour therefore belongs on the
 *    PARENT, which the glyph then inherits — the same `.ant-tag`-class trap
 *    AGENTS.md records for the seams that re-emit antd class names. Which
 *    parents those are, and the one token they all paint, is AGENTS.md's
 *    loading-states entry; do not keep a second list here.
 *
 * Accessibility is the captain's, unchanged and slightly better than antd's:
 * `role="status"` plus a name. antd's own Spin renders `aria-live="polite"`
 * and NO accessible name at all, so a screen reader announces nothing to
 * identify what is loading.
 *
 * THE `label` DEFAULT IS THE BOOT-PATH ANSWER, AND ONLY THAT. The English
 * literal is correct here because this primitive is reachable from the ONE
 * boot-static loading surface — `RouteChunkSkeleton`, which paints before any
 * locale has resolved and which therefore passes the literal explicitly. It is
 * the wrong answer everywhere else, so every composing surface that renders
 * inside a resolved locale translates it and passes it in: `src/ui/loading/
 * LoadingWall` and `src/antd/Spin/Spin.shadcn` both default to `t('Loading')`.
 * Note that the `Loading` source string is NOT registered with Transifex (the
 * same state the four Typography labels are in), so a DA or FI tenant falls
 * back to this English source until someone pushes it with real credentials —
 * accepted, not an oversight.
 * ------------------------------------------------------------------------- */
// `ref` is omitted along with `children`: React 17's `ComponentProps<'span'>`
// still admits a legacy STRING ref, which the icon dispatcher's
// `forwardRef<HTMLSpanElement>` does not take. Nothing needs a ref on a
// spinner; if something ever does, widen it here rather than at the call site.
export type SpinnerProps = Omit<
  React.ComponentProps<'span'>,
  'children' | 'ref'
> & {
  /** The accessible name announced when the spinner appears. */
  label?: string
}

function Spinner({ className, label = 'Loading', ...props }: SpinnerProps) {
  return (
    <LoadingOutlined
      role="status"
      aria-label={label}
      data-slot="spinner"
      className={cn('text-base', className)}
      {...props}
    />
  )
}

export { Spinner }
